import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ListChecks, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const WEEK_DATE_FORMATTER = new Intl.DateTimeFormat("es-EC", { day: "2-digit", month: "short" });
const dateRange = (week) => {
    const start = new Date(`${week.week_start}T12:00:00`);
    const end = new Date(`${week.week_end}T12:00:00`);
    return `${WEEK_DATE_FORMATTER.format(start)} – ${WEEK_DATE_FORMATTER.format(end)}`;
};

export default function WeeklyChecklistDialog({ item, open, onOpenChange, canManage, onChanged }) {
    const [weeks, setWeeks] = useState([]);
    const [currentWeekKey, setCurrentWeekKey] = useState("");
    const [selectedWeekKey, setSelectedWeekKey] = useState("");
    const [newText, setNewText] = useState("");
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const loadRequestRef = useRef(0);

    const load = async ({ preserveSelection = true } = {}) => {
        if (!item) return;
        const requestId = ++loadRequestRef.current;
        setLoading(true);
        try {
            const { data } = await api.get(`/weekly-control/items/${item.weekly_item_id}/checklists`);
            if (requestId !== loadRequestRef.current) return;
            const nextWeeks = data?.weeks || [];
            setWeeks(nextWeeks);
            setCurrentWeekKey(data?.current_week_key || "");
            setSelectedWeekKey((selected) => preserveSelection && nextWeeks.some((week) => week.week_key === selected)
                ? selected
                : (data?.current_week_key || nextWeeks[0]?.week_key || ""));
        } catch (error) {
            if (requestId !== loadRequestRef.current) return;
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            if (requestId === loadRequestRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!open || !item) return;
        setNewText("");
        setEditing(null);
        load({ preserveSelection: false });
        return () => { loadRequestRef.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, item?.weekly_item_id]);

    const selectedWeek = useMemo(
        () => weeks.find((week) => week.week_key === selectedWeekKey) || weeks[0] || null,
        [weeks, selectedWeekKey],
    );

    const refresh = async () => {
        await load();
        await onChanged?.();
    };

    const addItem = async (event) => {
        event.preventDefault();
        const text = newText.trim();
        if (!canManage || !text || !selectedWeek || savingId) return;
        setSavingId("new");
        try {
            await api.post(`/weekly-control/items/${item.weekly_item_id}/checklist-items`, {
                text,
                week_key: selectedWeek.week_key,
            });
            setNewText("");
            await refresh();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSavingId(null);
        }
    };

    const toggleItem = async (entry) => {
        if (!canManage) return;
        setSavingId(entry.checklist_item_id);
        try {
            await api.patch(`/weekly-control/items/${item.weekly_item_id}/checklist-items/${entry.checklist_item_id}`, {
                completed: !entry.completed,
            });
            await refresh();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSavingId(null);
        }
    };

    const removeItem = async (entry) => {
        if (!canManage || !window.confirm(`¿Eliminar “${entry.text}”?`)) return;
        setSavingId(entry.checklist_item_id);
        try {
            await api.delete(`/weekly-control/items/${item.weekly_item_id}/checklist-items/${entry.checklist_item_id}`);
            await refresh();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSavingId(null);
        }
    };

    const saveText = async (entry) => {
        const text = editing?.text?.trim();
        if (!canManage || !text || savingId) return;
        setSavingId(entry.checklist_item_id);
        try {
            await api.patch(`/weekly-control/items/${item.weekly_item_id}/checklist-items/${entry.checklist_item_id}`, { text });
            setEditing(null);
            await refresh();
        } catch (error) {
            toast.error(formatApiError(error.response?.data?.detail));
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
                <DialogHeader>
                    <DialogTitle>Checklist semanal · {item?.project_name}</DialogTitle>
                    <p className="text-sm text-slate-500">Cada semana conserva su propia lista. El historial anterior permanece disponible y editable.</p>
                </DialogHeader>
                {loading && !weeks.length ? <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500" aria-label="Cargando checklist" /></div> : (
                    <div className="grid min-h-0 flex-1 gap-4 overflow-hidden md:grid-cols-[210px_minmax(0,1fr)]">
                        <aside className="space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-2" aria-label="Historial de semanas">
                            {weeks.map((week) => (
                                <button type="button" key={week.week_key} onClick={() => setSelectedWeekKey(week.week_key)}
                                    className={`w-full rounded-md border p-3 text-left ${selectedWeekKey === week.week_key ? "border-orange-300 bg-orange-50" : "border-transparent hover:bg-slate-50"}`}>
                                    <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-900">{week.week_key === currentWeekKey ? "Semana actual" : week.week_key}</span><span className="text-xs text-slate-500">{week.completed}/{week.total}</span></div>
                                    <p className="mt-1 text-xs text-slate-500">{dateRange(week)}</p>
                                </button>
                            ))}
                        </aside>
                        <section className="min-h-0 overflow-y-auto rounded-lg border border-slate-200 p-4">
                            {selectedWeek && <>
                                <div className="mb-4 flex items-center justify-between gap-3">
                                    <div><h3 className="font-semibold text-slate-900">{selectedWeek.week_key === currentWeekKey ? "Semana actual" : selectedWeek.week_key}</h3><p className="text-xs text-slate-500">{dateRange(selectedWeek)}</p></div>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"><CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />{selectedWeek.completed}/{selectedWeek.total}</span>
                                </div>
                                <div className="space-y-2">
                                    {selectedWeek.items.map((entry) => (
                                        <div key={entry.checklist_item_id} className="flex items-start gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2.5">
                                            <Checkbox checked={Boolean(entry.completed)} disabled={!canManage || savingId === entry.checklist_item_id} onCheckedChange={() => toggleItem(entry)} aria-label={`${entry.completed ? "Marcar pendiente" : "Marcar completado"}: ${entry.text}`} className="mt-0.5" />
                                            {editing?.id === entry.checklist_item_id ? <div className="flex min-w-0 flex-1 gap-1"><Label htmlFor={`checklist-edit-${entry.checklist_item_id}`} className="sr-only">Editar punto</Label><Input id={`checklist-edit-${entry.checklist_item_id}`} autoFocus value={editing.text} maxLength={2000} onChange={(event) => setEditing((current) => ({...current, text:event.target.value}))} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); saveText(entry); } else if (event.key === "Escape") setEditing(null); }} className="h-8"/><Button size="icon" className="h-8 w-8" onClick={() => saveText(entry)} disabled={!editing.text.trim()} aria-label="Guardar texto"><Save className="h-3.5 w-3.5"/></Button><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(null)} aria-label="Cancelar edición"><X className="h-3.5 w-3.5"/></Button></div> : <span className={`min-w-0 flex-1 break-words text-sm ${entry.completed ? "text-slate-400 line-through" : "text-slate-700"}`}>{entry.text}</span>}
                                            {savingId === entry.checklist_item_id ? <Loader2 className="h-4 w-4 animate-spin text-orange-500" aria-hidden="true" /> : canManage && editing?.id !== entry.checklist_item_id && <><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({id:entry.checklist_item_id,text:entry.text})} aria-label={`Editar: ${entry.text}`}><Pencil className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" /></Button><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(entry)} aria-label={`Eliminar: ${entry.text}`}><Trash2 className="h-3.5 w-3.5 text-red-500" aria-hidden="true" /></Button></>}
                                        </div>
                                    ))}
                                    {!selectedWeek.items.length && <div className="flex flex-col items-center py-10 text-center text-slate-400"><ListChecks className="mb-2 h-8 w-8 text-slate-300" aria-hidden="true" /><p className="text-sm">Esta semana todavía no tiene puntos.</p></div>}
                                </div>
                                {canManage && <form onSubmit={addItem} className="mt-4 flex gap-2">
                                    <div className="flex-1"><Label htmlFor="weekly-checklist-new" className="sr-only">Nuevo punto del checklist</Label><Input id="weekly-checklist-new" value={newText} onChange={(event) => setNewText(event.target.value)} maxLength={2000} placeholder="Agregar un punto a esta semana…" /></div>
                                    <Button type="submit" disabled={!newText.trim() || savingId === "new"}>{savingId === "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}<span className="sr-only">Agregar punto</span></Button>
                                </form>}
                            </>}
                        </section>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
